import { atom } from "jotai";
import type { ReactFlowInstance } from "@xyflow/react";

/**
 * Atom that stores the full ReactFlow instance.
 *
 * This atom provides centralized access to all ReactFlow methods like:
 * - setViewport: For programmatically setting the canvas viewport position and zoom
 * - fitView: For automatically fitting the viewport to show all or selected nodes
 * - zoomIn/zoomOut: For programmatic zoom control
 * - getNodes/getEdges: For accessing current nodes and edges
 * - project/viewportToClient: For coordinate transformations
 *
 * The atom is populated in the CanvasHydrator component via useHydrateAtoms
 * after the useReactFlow() hook is available.
 *
 * @example
 * // To use in a component:
 * const reactFlowInstance = useAtomValue(reactFlowInstanceAtom);
 * if (reactFlowInstance) {
 *   reactFlowInstance.fitView();
 * }
 *
 * // To use in an atom:
 * const myAtom = atom(null, (get, set) => {
 *   const reactFlowInstance = get(reactFlowInstanceAtom);
 *   if (reactFlowInstance) {
 *     reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });
 *   }
 * });
 */
export const reactFlowInstanceAtom = atom<ReactFlowInstance | null>(null);
reactFlowInstanceAtom.debugLabel = "reactFlowInstanceAtom";
